import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';

export type IssueEvent = Readonly<
    | {
          type: 'activity';
          action: 'create' | 'update' | 'delete';
          activityId: string;
      }
    | {
          type: 'issue';
          action: 'create' | 'update';
          issueId: string;
      }
    | {
          type: 'flag';
          action: 'update';
          issueId: string;
          flagInfo: {
              flagged: boolean;
              shouldReceiveNotifications: boolean;
              isArchived: boolean;
          };
      }
    | {
          type: 'task';
          action: 'create' | 'update' | 'delete';
          workItemId: string;
      }
>;

export type IssueEventCallback = (event: IssueEvent) => void;

export type IssueEventsContext = Readonly<{
    registerListener: (callback: IssueEventCallback) => () => void;
    triggerIssueEvent: (event: IssueEvent) => void;
    destroy: () => void;
}>;

function buildIssueEventContext() {
    const callbacks = new Set<IssueEventCallback>();

    const contextValue: IssueEventsContext = {
        registerListener: (callback) => {
            callbacks.add(callback);

            return () => {
                callbacks.delete(callback);
            };
        },
        triggerIssueEvent: (event) => callbacks.forEach((callback) => callback(event)),
        destroy: () => callbacks.clear(),
    };

    return contextValue;
}

const Context = createContext<IssueEventsContext | undefined>(undefined);

export type IssueEventsProviderProps = Readonly<{ children?: ReactNode }>;

export function IssueEventsProvider({ children }: IssueEventsProviderProps) {
    const value: IssueEventsContext = useMemo(() => buildIssueEventContext(), []);

    useEffect(() => () => value.destroy(), [value]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

function useIssueEventsContext() {
    const ctx = useContext(Context);

    if (!ctx) {
        throw new Error('Missing <IssueEventsProvider>');
    }

    return ctx;
}

export function useOnIssueEvent(callback: IssueEventCallback) {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const { registerListener } = useIssueEventsContext();

    useEffect(() => {
        const unregister = registerListener((event) => callbackRef.current(event));
        return unregister;
    }, [registerListener]);
}

export function useTriggerIssueEvent() {
    return useIssueEventsContext().triggerIssueEvent;
}
